/* ============================================================================
   GLO-MOD 2026-08-15 — Variant-scoped gallery media (EXPERIMENT — may revert)

   WHY: products with many variants (Fun Fillers: 9 variants, 27 photos) show
   every variant's photos in one gallery, making the desktop media column very
   tall and the gallery confusing. Shapes 5.0.1 has no native "show only the
   selected variant's media" option, and we chose this over an app.

   HOW IT WORKS (the whole convention):
   - In Shopify admin, a product photo is "assigned" to variants by starting
     its ALT TEXT with one or more bracket tags, e.g.:
         [Under the Sea] Kids playing with the ocean set
         [Under the Sea][Silly Shapes] shared between two colors
   - A tag matches when it equals any of the selected variant's option values
     (case-insensitive): for variant "Under the Sea" the tag is
     [Under the Sea].
   - Photos with NO bracket tag are shared — they show for every variant.
   - Products with zero tagged photos are completely untouched (this script
     is inert everywhere until someone tags an alt), so shipping it site-wide
     is safe.
   - The bracket prefix is stripped from the alt at runtime so screen readers
     never hear it.

   HOW TO REVERT: delete this file and the single render line marked
   "GLO-MOD variant media" in layout/theme.liquid. Alt-text tags left behind
   are harmless (they'd only be read by screen readers again — strip them in
   admin at leisure).

   KNOWN LIMITS (v1, on purpose):
   - If a product's gallery uses the synced thumbnail strip, we skip
     filtering entirely rather than risk desyncing the two carousels.
     (Fun Fillers doesn't use thumbnails.)
   - Only the main product gallery is filtered — not quick-buy drawers.
   ========================================================================== */
(function () {
  'use strict';

  var TAG_PREFIX = /^(\s*\[[^\]]+\])+\s*/; // one or more leading [tags]
  var TAG_EACH = /\[([^\]]+)\]/g;

  function parseTags(alt) {
    var m = (alt || '').match(TAG_PREFIX);
    if (!m) return null; // untagged -> shared media
    var tags = [];
    var each;
    while ((each = TAG_EACH.exec(m[0])) !== null) tags.push(each[1].trim().toLowerCase());
    return tags;
  }

  function setupGallery(gallery) {
    // idempotence guard (2026-08-25): no-reload product swaps re-run
    // init over old + new galleries
    if (gallery.__gpVariantMedia) return;
    gallery.__gpVariantMedia = true;
    // GLO 2026-08-17: synced thumbnail carousels are now MANAGED, not
    // bailed on (the v1 limit) — thumbnails moved to a synced Splide when
    // desktop position went "left", which silently disabled filtering.
    // We filter the paired thumbnail lists in the SAME order as the main
    // list, so the synced indexes stay aligned.
    var thumbTargets = [];
    if (gallery.dataset.thumbnails) {
      var sectionKey = gallery.dataset.thumbnails.replace('slideshow-thumbnails-', '');
      ['slideshow-thumbnails-' + sectionKey, 'slideshow-mobile-thumbnails-' + sectionKey].forEach(function (id) {
        var wrap = document.getElementById(id);
        var tl = wrap && wrap.querySelector('.splide__list');
        if (tl) thumbTargets.push({ wrap: wrap, list: tl, meta: null });
      });
      if (gallery.dataset.thumbnails && !thumbTargets.length) return; // unknown structure: stay inert
    }

    var list = gallery.querySelector('.feature-media-list');
    if (!list) return;

    var slides = Array.prototype.slice.call(list.children);
    var tagged = 0;
    var tagsByMediaId = {};
    var meta = slides.map(function (slide) {
      var img = slide.querySelector('img');
      var tags = img ? parseTags(img.alt) : null;
      if (tags) {
        tagged++;
        // strip the [tag] prefix so assistive tech reads a clean alt
        img.alt = img.alt.replace(TAG_PREFIX, '');
        var mid = slide.getAttribute('data-media-id');
        if (mid) tagsByMediaId[mid] = tags;
      }
      return { el: slide, tags: tags };
    });
    if (tagged === 0) return; // nothing tagged -> feature stays inert

    thumbTargets.forEach(function (t) {
      t.meta = Array.prototype.slice.call(t.list.children).map(function (el) {
        return { el: el, mid: el.getAttribute('data-media-id') };
      });
      // strip [tag] prefixes from thumb alts too
      t.meta.forEach(function (m) {
        var img = m.el.querySelector('img');
        if (img) img.alt = img.alt.replace(TAG_PREFIX, '');
      });
    });

    // The desktop THUMBNAIL GRID (when it isn't a synced carousel) lists the
    // same media as li[data-media-id] — it's what makes the media column
    // tall, so it gets the same treatment, matched by media id.
    var mediaColumn = gallery.closest('.product-media') || gallery.parentElement;
    var thumbs = Array.prototype.slice.call(
      mediaColumn.querySelectorAll('li.product-thumbnail-list-item[data-media-id]')
    );
    thumbs.forEach(function (t) {
      var img = t.querySelector('img');
      if (img) img.alt = img.alt.replace(TAG_PREFIX, '');
    });

    function apply(optionValues) {
      var values = optionValues.map(function (v) { return String(v).trim().toLowerCase(); });
      var matches = function (tags) {
        return !tags || tags.some(function (t) { return values.indexOf(t) !== -1; });
      };
      // iOS Safari: detaching slides collapses the track height for a beat
      // (until its scroll-driven resize), so the buy card jumped up over
      // the gallery. Pin the current height through the swap and release
      // once the new first image is ready, with a fresh refresh().
      var prevH = gallery.offsetHeight;
      if (prevH > 0) gallery.style.minHeight = prevH + 'px';
      // ALSO pin the outer media wrapper — it is what separates the media
      // from the buy column in flow. Whatever collapses inside (Safari
      // computes Splide track heights differently), the wrapper holds and
      // the content below cannot ride up over the image.
      var wrap = gallery.closest('.product-media') || gallery.parentElement;
      var wrapH = wrap ? wrap.offsetHeight : 0;
      if (wrap && wrapH > 0) wrap.style.minHeight = wrapH + 'px';
      // Safari scroll anchoring: the slide swap above the viewport makes
      // Safari adjust scrollTop (page appears to leap up on the FIRST tap
      // per variant, when image sizes change). Pin the scroll position
      // across the mutation and the async settle.
      var prevScroll = window.pageYOffset;
      var guardScroll = function () {
        if (Math.abs(window.pageYOffset - prevScroll) > 40) {
          window.scrollTo(0, prevScroll);
        }
      };
      // Non-matching slides are physically DETACHED from the track (not just
      // display:none): Splide keeps hidden slides in its registry, so a
      // swipe could land on another variant's photo. Detaching means Splide
      // only ever knows about the current variant's slides. `meta` keeps the
      // detached elements alive (in original order) for the next variant.
      meta.forEach(function (m) {
        if (m.el.parentNode === list) list.removeChild(m.el);
      });
      meta.forEach(function (m) {
        if (matches(m.tags)) list.appendChild(m.el);
      });
      // The gallery defers image src to data-gp-src and hydrates at window
      // load (GLO-MOD in product-template.liquid). Slides we detached before
      // that moment missed hydration — re-run it so re-attached slides get
      // their src back (it's idempotent: only touches img[data-gp-src]).
      if (window.gpHydrateGallery) window.gpHydrateGallery();
      // Synced thumbnail carousels: detach/re-attach in the same order as
      // the main list so Splide sync indexes keep matching, then refresh.
      thumbTargets.forEach(function (t) {
        t.meta.forEach(function (m) {
          if (m.el.parentNode === t.list) t.list.removeChild(m.el);
        });
        t.meta.forEach(function (m) {
          if (matches(tagsByMediaId[m.mid] || null)) t.list.appendChild(m.el);
        });
        try {
          var tinst = window.slideshows && window.slideshows[t.wrap.id];
          if (tinst && t.wrap.classList.contains('is-initialized')) tinst.refresh();
        } catch (e) { /* thumbnails must never break the PDP */ }
      });
      // Plain-grid thumbnails (non-synced layouts): hiding is enough there.
      thumbs.forEach(function (t) {
        var show = matches(tagsByMediaId[t.getAttribute('data-media-id')] || null);
        t.toggleAttribute('hidden', !show);
        t.style.display = show ? '' : 'none';
      });
      // If Splide runs this gallery (mobile carousel), refresh() re-collects
      // the track's slides from the DOM so length/pagination match what's
      // attached. Guarded: desktop stacked layouts may have no instance.
      var inst = null;
      try {
        inst = window.slideshows && window.slideshows[gallery.id];
        if (inst && gallery.classList.contains('is-initialized')) {
          inst.refresh();
          inst.go(0); // start at the new variant's first photo
        }
      } catch (e) { /* never let gallery filtering break the PDP */ }
      requestAnimationFrame(guardScroll);
      setTimeout(guardScroll, 120);
      var released = false;
      var releaseTries = 0;
      var release = function () {
        if (released) return;
        try {
          if (inst && gallery.classList.contains('is-initialized')) inst.refresh();
        } catch (e) { /* ignore */ }
        // only drop the pins once the SLIDES demonstrably have height
        // again (the pinned box always measures tall) — else hold and
        // retry (max ~3s)
        var slideH = list.firstElementChild ? list.firstElementChild.offsetHeight : 0;
        if (slideH < 40 && releaseTries++ < 6) {
          setTimeout(release, 500);
          return;
        }
        released = true;
        gallery.style.minHeight = '';
        if (wrap) wrap.style.minHeight = '';
        guardScroll();
      };
      var firstImg = list.querySelector('img');
      if (firstImg && !firstImg.complete) {
        firstImg.addEventListener('load', release, { once: true });
        setTimeout(release, 1500); // belt & suspenders on slow/cached loads
      } else {
        requestAnimationFrame(release);
      }
    }

    // Initial pass: read the currently selected options straight from the
    // variant picker inputs (the island's first variant event can fire
    // before we're listening). Rev 2026-08-25: after a no-reload product
    // swap the picker's checked state isn't settled yet when we set up —
    // retry a couple of beats so the swapped-in gallery starts filtered.
    // rev 2026-08-25: the gallery's OWN id ends in __main too, so the old
    // [id$="__main"] closest() matched the gallery itself and the picker
    // inputs were never found — the initial pass silently no-oped (fresh
    // loads were saved by the island's boot variant event; post-swap
    // there's no such event after we bind, so it finally surfaced).
    var root = gallery.closest('.shopify-section') || document.body;
    var applyInitial = function () {
      var checked = root.querySelectorAll('.product-block-variant-picker input:checked, .product-block-variant-picker select');
      var initial = Array.prototype.map.call(checked, function (i) { return i.value; });
      if (initial.length) { apply(initial); return true; }
      // rev 2026-10-20 (Hagan: Firefly jar showed every color's photos after
      // a swatch tap): family members with colors have NO variant picker
      // since 10-19 - their colors are family chips. Read the selected
      // variant (form id, else ?variant=) and take its chip's color name.
      // wait for the swapped-in product island to hydrate - moving slides
      // and thumbnails before Alpine binds them throws ReferenceErrors
      // (currentMediaId) from the stock thumbnail bindings
      var island = gallery.closest('data-island');
      if (island && !island.hasAttribute('ready')) return false;
      var chip = root.querySelector('[data-gp-member] a.is-selected[title], a[data-gp-member].is-selected[title]');
      if (!chip) {
        var idEl = root.querySelector('input[name="id"]');
        var vid = idEl && idEl.value;
        if (!vid) {
          var m = /[?&]variant=(\d+)/.exec(window.location.search);
          vid = m && m[1];
        }
        chip = vid && root.querySelector('[data-gp-member] a[href*="variant=' + vid + '"][title], a[data-gp-member][href*="variant=' + vid + '"][title]');
      }
      var name = chip && chip.getAttribute('title').trim().toLowerCase();
      // only when this gallery is tagged for that color (untagged galleries
      // and single-variant members stay untouched)
      var tagged = name && Object.keys(tagsByMediaId).some(function (k) {
        return (tagsByMediaId[k] || []).indexOf(name) !== -1;
      });
      if (tagged) { apply([chip.getAttribute('title')]); return true; }
      return false;
    };
    if (!applyInitial()) {
      // post-swap hydration can take a couple seconds — keep trying
      // until the picker reports a selection (capped ~4s)
      var initTries = 0;
      var initTimer = setInterval(function () {
        if (applyInitial() || ++initTries > 10) clearInterval(initTimer);
      }, 400);
    }

    // Follow every variant change (fired by island-product on document)
    document.addEventListener('theme:variant:change', function (e) {
      var v = e.detail && e.detail.variant;
      if (!v) return;
      // only react to events for the product root that owns this gallery
      if (e.detail.productRootEl && !e.detail.productRootEl.contains(gallery)) return;
      var values = [v.option1, v.option2, v.option3].filter(Boolean);
      if (values.length) apply(values);
    });
  }

  function init() {
    document.querySelectorAll('.splide--product').forEach(setupGallery);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  // 2026-08-25: the group picker's no-reload swap replaces the product
  // section wholesale — watch for new galleries and set them up too
  // (setupGallery is guarded, so re-running over old ones is free)
  var gpReinitT = null;
  new MutationObserver(function () {
    clearTimeout(gpReinitT);
    gpReinitT = setTimeout(init, 120);
  }).observe(document.body, { childList: true, subtree: true });
})();
